/**
 * MaintenanceContext — customer-front state for the Plan-MaintenanceBanners
 * Phase E.1 ship.
 *
 * Sources of truth (in order of priority):
 *   1. Cold-load REST  : GET /api/system/maintenance on mount.
 *   2. Live SSE       : 'maintenance_state' event piggybacked on the
 *                       existing /api/notifications/stream connection
 *                       (handled in Layout.tsx — fans out via the
 *                       'app:maintenance_state' CustomEvent so this
 *                       context never opens a second EventSource).
 *   3. 10-min poll    : safety net for missed SSE events / connection
 *                       drops / backend not yet shipped (Storm's Phase 2
 *                       SSE piggyback may not have rolled out at the
 *                       same moment as this front). Relaxed from 30s
 *                       on 2026-05-11 once SSE proved to be the primary
 *                       signal in production — poll is now a slow
 *                       reconcile, not a primary update channel.
 *   4. Axios 503 hook : 'maintenance:detected' CustomEvent fired by the
 *                       services/api.ts interceptor when ANY API call
 *                       returns 503 with `reason=maintenance_ongoing` —
 *                       force-flips state to ongoing without waiting
 *                       for the next SSE / poll tick.
 *
 * 404 / network error / timeout on the cold-load fetch silently defaults
 * to mode=off — the backend may not be deployed yet OR may briefly 404
 * during a deploy cutover, and the customer app must NEVER crash on a
 * missing/broken maintenance endpoint.
 */
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import { parseServerIso } from '../utils/datetime';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
// Relaxed from 30_000 to 600_000 (10 min) on 2026-05-11. SSE event
// `app:maintenance_state` (re-broadcast by Layout.tsx from the
// /api/notifications/stream EventSource) is the primary signal, and the
// axios 503 'maintenance:detected' interceptor force-flips us into
// ongoing on the very first blocked request. The poll exists ONLY as a
// safety net for missed SSE / dropped EventSource / backend not yet
// shipped — 10 min is fast enough to recover from those edge cases and
// avoids 120× /api/system/maintenance hits per hour per browser tab.
const POLL_INTERVAL_MS = 600_000;
const COLD_LOAD_TIMEOUT_MS = 5_000;

export type MaintenanceMode = 'off' | 'upcoming' | 'ongoing';
export type MaintenanceScope = 'full' | 'app_only';

export interface MaintenancePayload {
  mode: MaintenanceMode;
  scope: MaintenanceScope;
  starts_at: string | null;
  ends_at: string | null;
  message_title: string | null;
  message_body: string | null;
  lead_h?: number | null;
  version?: string | null;
}

export interface MaintenanceState {
  mode: MaintenanceMode;
  scope: MaintenanceScope;
  startsAt: Date | null;
  endsAt: Date | null;
  messageTitle: string | null;
  messageBody: string | null;
  isUpcoming: boolean;
  isOngoing: boolean;
}

const DEFAULT_STATE: MaintenanceState = {
  mode: 'off',
  scope: 'full',
  startsAt: null,
  endsAt: null,
  messageTitle: null,
  messageBody: null,
  isUpcoming: false,
  isOngoing: false,
};

const MaintenanceContext = createContext<MaintenanceState>(DEFAULT_STATE);

function isValidMode(m: unknown): m is MaintenanceMode {
  return m === 'off' || m === 'upcoming' || m === 'ongoing';
}

function isValidScope(s: unknown): s is MaintenanceScope {
  return s === 'full' || s === 'app_only';
}

function payloadToState(p: Partial<MaintenancePayload> | null | undefined): MaintenanceState {
  if (!p) return DEFAULT_STATE;
  const mode: MaintenanceMode = isValidMode(p.mode) ? p.mode : 'off';
  const scope: MaintenanceScope = isValidScope(p.scope) ? p.scope : 'full';
  const startsAt = parseServerIso(p.starts_at);
  const endsAt = parseServerIso(p.ends_at);
  return {
    mode,
    scope,
    // parseServerIso already collapses missing/invalid to null.
    startsAt,
    endsAt,
    messageTitle: p.message_title ?? null,
    messageBody: p.message_body ?? null,
    isUpcoming: mode === 'upcoming',
    isOngoing: mode === 'ongoing',
  };
}

export const MaintenanceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<MaintenanceState>(DEFAULT_STATE);
  // Keep latest state in a ref so the SSE/event handlers (registered once)
  // can read it without being re-bound on every state change.
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Apply a payload only when it differs from current state (avoids
  // pointless re-renders + downstream effect storms).
  const applyPayload = (payload: Partial<MaintenancePayload> | null | undefined) => {
    const next = payloadToState(payload);
    setState((prev) => {
      if (
        prev.mode === next.mode &&
        prev.scope === next.scope &&
        prev.messageTitle === next.messageTitle &&
        prev.messageBody === next.messageBody &&
        prev.startsAt?.getTime() === next.startsAt?.getTime() &&
        prev.endsAt?.getTime() === next.endsAt?.getTime()
      ) {
        return prev;
      }
      return next;
    });
  };

  const fetchOnce = async (signal?: AbortSignal): Promise<void> => {
    try {
      const res = await axios.get<MaintenancePayload>(
        `${API_URL}/api/system/maintenance`,
        {
          // Public endpoint — no auth required. Don't send credentials so
          // we don't trip CORS preflight unnecessarily on early boot.
          withCredentials: false,
          timeout: COLD_LOAD_TIMEOUT_MS,
          signal,
          // Backend sets `Cache-Control: no-store` per §C.3 — bypass any
          // intermediate caches that may have stuck on the old payload.
          headers: { 'Cache-Control': 'no-cache' },
        },
      );
      applyPayload(res.data);
    } catch {
      // 404 / network error / timeout / CORS / abort — silently default
      // to mode=off. The backend may not be deployed yet, may be briefly
      // unavailable during a deploy cutover, or may simply have no
      // public endpoint mounted. In every case the customer app must
      // continue to function as if no maintenance is scheduled.
      // (We do NOT clear existing state — if a prior SSE / 503 hint
      // already flipped us into ongoing, a transient poll failure should
      // not roll us back to off.)
    }
  };

  // Cold-load fetch + 10-min polling fallback (see POLL_INTERVAL_MS comment).
  useEffect(() => {
    const controller = new AbortController();
    fetchOnce(controller.signal);
    const id = window.setInterval(() => {
      fetchOnce();
    }, POLL_INTERVAL_MS);
    return () => {
      controller.abort();
      window.clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SSE event piggyback: Layout.tsx owns the EventSource and re-broadcasts
  // 'maintenance_state' as the 'app:maintenance_state' CustomEvent. We
  // listen for that here.
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<MaintenancePayload>;
      if (!ce.detail) return;
      applyPayload(ce.detail);
    };
    window.addEventListener('app:maintenance_state', handler as EventListener);
    return () => {
      window.removeEventListener('app:maintenance_state', handler as EventListener);
    };
  }, []);

  // Axios interceptor force-update: when ANY 503 response carries
  // `reason=maintenance_ongoing`, jump to ongoing immediately so the
  // blocker renders without waiting for the next SSE/poll tick.
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{
        mode?: string;
        scope?: string;
        message_title?: string;
        message_body?: string;
        ends_at?: string;
      }>;
      if (!ce.detail) return;
      const cur = stateRef.current;
      // Only escalate — don't silently downgrade off→ongoing→off via this path.
      applyPayload({
        mode: 'ongoing',
        scope: isValidScope(ce.detail.scope) ? ce.detail.scope : cur.scope,
        starts_at: cur.startsAt ? cur.startsAt.toISOString() : null,
        ends_at: ce.detail.ends_at ?? (cur.endsAt ? cur.endsAt.toISOString() : null),
        message_title: ce.detail.message_title ?? cur.messageTitle,
        message_body: ce.detail.message_body ?? cur.messageBody,
      });
    };
    window.addEventListener('maintenance:detected', handler as EventListener);
    return () => {
      window.removeEventListener('maintenance:detected', handler as EventListener);
    };
  }, []);

  // Re-render the consumers (banner countdown, blocker end-time) on a
  // tight tick so derived flags like isUpcoming/isOngoing stay accurate
  // when the operator-set window crosses now without an SSE event (pure
  // clock-derived transition). 1-minute cadence is cheap and matches the
  // banner's countdown granularity.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => (t + 1) % 1_000_000), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const value = useMemo<MaintenanceState>(() => state, [state]);

  return (
    <MaintenanceContext.Provider value={value}>
      {children}
    </MaintenanceContext.Provider>
  );
};

export const useMaintenance = (): MaintenanceState => useContext(MaintenanceContext);
